import * as React from "react";
import { RiArrowDownSLine, RiArrowUpSLine } from "@remixicon/react";
import { cn } from "~/lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";

export interface ComboboxOption {
  id: string;
  display: string;
}

export interface ComboboxProps {
  /** Which field this is (e.g. "make"); becomes part of the input's data-sid. */
  sid: string;
  id?: string;
  options: ComboboxOption[];
  value: ComboboxOption | null;
  onValueChange: (id: string) => void;
  placeholder?: string;
  emptyMessage?: React.ReactNode;
  disabled?: boolean;
  // Announced via aria-required, not native `required`, so it never triggers browser
  // form validation.
  required?: boolean;
  className?: string;
}

// Type-to-filter combobox (Popover + Command) — the visible field is itself the filter
// input, not a button that opens a separate search box, matching Figma's expand-in-place
// combo (e.g. typing "do" filters down to "Dodge").
export function Combobox({ id, sid, options, value, onValueChange, placeholder, emptyMessage, disabled, required, className }: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState(value?.display ?? "");
  // The field shows the selected label, so only filter once the user has typed —
  // otherwise reopening after a pick would filter down to just that one option.
  const [isFiltering, setIsFiltering] = React.useState(false);

  // Keeps the field in sync when its value is changed from outside (e.g. Model resets
  // when Make changes) without clobbering text the user is actively typing.
  const [seenValue, setSeenValue] = React.useState(value);
  if (value !== seenValue) {
    setSeenValue(value);
    setSearch(value?.display ?? "");
    setIsFiltering(false);
  }

  // Portal into the enclosing modal Dialog (e.g. the Drawer), if any. Modal Dialogs
  // block wheel/touch scrolling outside their own subtree, so a list portaled to
  // <body> can't be scrolled; inside the Dialog it scrolls normally.
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);
  const triggerRef = React.useCallback((node: HTMLDivElement | null) => {
    setPortalContainer(node?.closest<HTMLElement>('[role="dialog"]') ?? null);
  }, []);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setSearch(value?.display ?? "");
      setIsFiltering(false);
    }
  }

  const filtered =
    isFiltering && search ? options.filter((o) => o.display.toLowerCase().includes(search.toLowerCase())) : options;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          ref={triggerRef}
          className={cn(
            "border-figma-button-border-subtle rounded-figma-radius-radius-md bg-figma-background-surface-bg-primary gap-figma-spacing-sm flex w-full items-center border",
            disabled && "bg-figma-background-surface-disabled text-figma-text-cta-disabled pointer-events-none border-transparent",
            className,
          )}
        >
          <input
            data-sid={`combobox-input:${sid}`}
            id={id}
            type="text"
            aria-required={required}
            disabled={disabled}
            value={search}
            placeholder={placeholder}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsFiltering(true);
              if (!open) setOpen(true);
            }}
            className="placeholder:text-figma-text-body-secondary-alt disabled:placeholder:text-figma-text-cta-disabled w-full bg-transparent outline-none disabled:cursor-not-allowed"
          />
          {/* Figma: Icon/Secondary when enabled; disabled inherits Text/CTA/Disabled above. */}
          {open ? (
            <RiArrowUpSLine className={cn("size-5 shrink-0", !disabled && "text-figma-icon-secondary")} />
          ) : (
            <RiArrowDownSLine className={cn("size-5 shrink-0", !disabled && "text-figma-icon-secondary")} />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        container={portalContainer}
        // pointer-events-auto: a modal Dialog sets body { pointer-events: none }; this
        // re-enables clicks when there's no Dialog to portal into.
        className="pointer-events-auto w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filtered.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => {
                    onValueChange(option.id);
                    setSearch(option.display);
                    setIsFiltering(false);
                    setOpen(false);
                  }}
                >
                  {option.display}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
